/* FD homepage fabric strip: the progress track, arrow-key paging and the
   simple zoom dialog. Everything here is an enhancement of markup that already
   works: the track ships hidden and is only un-hidden once the row is measured
   to overflow, the cue ships visible and is only hidden once the row is
   measured to fit, and a swatch is an <a> to the fabrics page (or a <button>
   when the merchant clears that link), so a browser without JavaScript gets a
   scrollable row of real links and no broken chrome.

   Deliberately NOT the PDP lightbox: this dialog carries no zoom, no rail and
   no slide stepping, so it shares nothing with fd-lightbox.js. */
(() => {
  const INIT = 'data-fd-strip-init';
  const SCROLLABLE = 'is-scrollable';
  const OPEN = 'is-open';

  const reduced = () =>
    window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---- the row, its track and its cue ---- */
  function initStrip(root) {
    const wrap = root.querySelector('[data-fd-strip]');
    if (!wrap) return;
    const rail = wrap.querySelector('[data-fd-strip-rail]');
    if (!rail) return;
    const track = wrap.querySelector('[data-fd-strip-track]');
    const thumb = wrap.querySelector('[data-fd-strip-thumb]');
    const cue = wrap.querySelector('[data-fd-strip-cue]');

    function paintThumb() {
      if (!track || !thumb || track.hidden) return;
      const visible = rail.clientWidth;
      const total = rail.scrollWidth;
      if (!total) return;
      const ratio = Math.min(1, visible / total);
      const max = total - visible;
      const travel = track.clientWidth * (1 - ratio);
      const pos = max > 0 ? (rail.scrollLeft / max) * travel : 0;
      thumb.style.width = ratio * 100 + '%';
      thumb.style.transform = 'translateX(' + Math.round(pos) + 'px)';
    }

    function measure() {
      const over = rail.scrollWidth > rail.clientWidth + 1;
      wrap.classList.toggle(SCROLLABLE, over);
      if (track) track.hidden = !over;
      if (cue) cue.hidden = !over;
      paintThumb();
    }

    rail.addEventListener('scroll', paintThumb, { passive: true });

    /* One tile plus one gap per press, so paging lands on a snap point. */
    rail.addEventListener('keydown', (event) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      const tile = rail.querySelector('.fd-sw');
      if (!tile) return;
      const gap = parseFloat(getComputedStyle(rail).columnGap) || 0;
      const step = tile.getBoundingClientRect().width + gap;
      event.preventDefault();
      rail.scrollBy({
        left: event.key === 'ArrowRight' ? step : -step,
        behavior: reduced() ? 'auto' : 'smooth',
      });
    });

    let ro = null;
    if ('ResizeObserver' in window) {
      ro = new ResizeObserver(measure);
      ro.observe(rail);
    }

    let pending = false;
    const onResize = () => {
      if (pending) return;
      pending = true;
      requestAnimationFrame(() => {
        pending = false;
        measure();
      });
    };
    window.addEventListener('resize', onResize);

    /* rAF so the tiles have been laid out (and the fonts settled) before the
       first measurement; a wrong first measurement would flash the track. */
    requestAnimationFrame(measure);

    /* The only two things here that outlive their section: every other
       listener is bound to a node the theme editor discards with the section,
       so a re-render collects them on its own. These two hang off window and
       would pile up one stale closure per editor re-render. */
    return () => {
      window.removeEventListener('resize', onResize);
      if (ro) ro.disconnect();
    };
  }

  /* ---- the zoom dialog ---- */
  function initZoom(root) {
    const dialog = root.querySelector('[data-fd-zoom-dialog]');
    if (!dialog) return;
    const img = dialog.querySelector('[data-fd-zoom-img]');
    const nameEl = dialog.querySelector('[data-fd-zoom-cap-name]');
    const subEl = dialog.querySelector('[data-fd-zoom-cap-sub]');
    const cta = dialog.querySelector('[data-fd-zoom-cta]');
    if (!img || !nameEl) return;

    let opener = null;
    let isOpen = false;
    let lockedOverflow = '';

    function teardown() {
      if (!isOpen) return;
      isOpen = false;
      dialog.classList.remove(OPEN);
      document.documentElement.style.overflow = lockedOverflow;
      img.removeAttribute('src');
      img.alt = '';
      if (opener) {
        opener.focus();
        opener = null;
      }
    }

    function close() {
      if (typeof dialog.close === 'function' && dialog.open) {
        dialog.close(); /* fires 'close', which tears down */
      } else {
        dialog.removeAttribute('open');
        teardown();
      }
    }

    function open(tile) {
      opener = tile;
      isOpen = true;
      img.src = tile.getAttribute('data-fd-zoom') || '';
      const name = tile.getAttribute('data-fd-zoom-name') || '';
      img.alt = name;
      nameEl.textContent = name;
      const sub = tile.getAttribute('data-fd-zoom-sub') || '';
      if (subEl) {
        subEl.textContent = sub;
        subEl.hidden = sub === '';
      }
      const href = tile.getAttribute('data-fd-zoom-href') || '';
      if (cta) {
        /* Never a dead link: a family with no collection shows no button. */
        cta.hidden = href === '';
        if (href) cta.href = href;
      }
      lockedOverflow = document.documentElement.style.overflow;
      document.documentElement.style.overflow = 'hidden';
      if (typeof dialog.showModal === 'function') {
        dialog.showModal();
      } else {
        /* Old Safari: no top layer, no native Escape. The scrim and the x
           still close it, which is the whole close contract minus Escape. */
        dialog.setAttribute('open', '');
      }
      requestAnimationFrame(() => dialog.classList.add(OPEN));
    }

    root.addEventListener('click', (event) => {
      const tile = event.target.closest('[data-fd-zoom]');
      if (!tile || !root.contains(tile)) return;
      event.preventDefault();
      open(tile);
    });

    /* The card is the dialog's only child and the dialog paints the scrim, so
       a click whose target IS the dialog is a click outside the card. */
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) close();
      else if (event.target.closest('[data-fd-zoom-close]')) close();
    });

    /* Escape is native on showModal(); this is what runs after it. */
    dialog.addEventListener('close', teardown);
  }

  /* root -> the teardown initStrip handed back, so an unload can undo it. */
  const teardowns = new Map();

  function init(root) {
    if (root.hasAttribute(INIT)) return;
    root.setAttribute(INIT, '');
    const off = initStrip(root);
    if (off) teardowns.set(root, off);
    initZoom(root);
  }

  function destroy(root) {
    const off = teardowns.get(root);
    if (!off) return;
    teardowns.delete(root);
    off();
  }

  function initAll(scope) {
    (scope || document).querySelectorAll('[data-fd-fabrics]').forEach(init);
  }

  initAll();

  /* A theme-editor re-render replaces the whole section, so the fresh copy
     initialises itself and the marker keeps the old listeners from doubling. */
  document.addEventListener('shopify:section:load', (event) => {
    initAll(event.target);
  });

  /* Shopify fires this while the outgoing section is still in the document, so
     the same lookup as the load handler still reaches the root being replaced. */
  document.addEventListener('shopify:section:unload', (event) => {
    (event.target || document).querySelectorAll('[data-fd-fabrics]').forEach(destroy);
  });
})();
